// src/hooks/useBoardMembership.ts
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * Map of collection IDs to their membership status (true if ad is in the board)
 */
interface BoardMembershipMap { [collectionId: string]: boolean; }

/**
 * Custom hook for managing board (collection) membership for an ad
 *
 * Handles fetching current board memberships, tracking pending changes,
 * and committing those changes to the database. Supports optimistic UI updates
 * by maintaining a separate pending changes state.
 *
 * @param adId - The ad ID to manage board membership for (null if no ad selected)
 *
 * @returns Object containing:
 *   - membership: Map of collectionId to boolean (current saved state in DB)
 *   - loading: Boolean indicating if initial fetch is in progress
 *   - isBoardChecked: Function to check if an ad is in a board (considers pending changes)
 *   - toggleBoard: Function to toggle board membership (collectionId: string, next?: boolean)
 *   - selectAll: Function to select all boards (collectionIds: string[])
 *   - deselectAll: Function to deselect all boards (collectionIds: string[])
 *   - selectOnly: Function to exclusively select specific boards (collectionIds: string[], allCollectionIds: string[])
 *   - commitChanges: Async function to save all pending changes to database
 *   - hasPendingChanges: Boolean indicating if there are uncommitted changes
 *   - pendingChanges: Map of collectionId to boolean (pending changes not yet saved)
 *   - savedBoardsCount: Number of boards the ad is currently saved to
 *
 * @example
 * ```tsx
 * const {
 *   loading,
 *   isBoardChecked,
 *   toggleBoard,
 *   selectAll,
 *   deselectAll,
 *   selectOnly,
 *   commitChanges,
 *   hasPendingChanges,
 *   savedBoardsCount
 * } = useBoardMembership(currentAdId);
 *
 * // Toggle board membership (will be pending until committed)
 * toggleBoard('collection-123');
 *
 * // Force add to board
 * toggleBoard('collection-456', true);
 *
 * // Add to all boards at once
 * selectAll(['collection-1', 'collection-2', 'collection-3']);
 *
 * // Remove from all boards at once
 * deselectAll(['collection-1', 'collection-2', 'collection-3']);
 *
 * // Exclusively select specific boards (deselects all others)
 * const allBoards = ['board-1', 'board-2', 'board-3', 'board-4'];
 * const selectedBoards = ['board-1', 'board-3'];
 * selectOnly(selectedBoards, allBoards);
 *
 * // Save all pending changes
 * await commitChanges();
 *
 * // Check if ad is in a board (includes pending changes)
 * const isInBoard = isBoardChecked('collection-123');
 * ```
 */
export function useBoardMembership(adId: string | null) {
  const [membership, setMembership] = useState<BoardMembershipMap>({});
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<BoardMembershipMap>({});

  useEffect(() => {
    if (!adId) {
      setMembership({});
      setPendingChanges({});
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('user_collection_items' as any)
          .select('collection_id')
          .eq('ad_id', adId);
        if (error) throw error;

        const map: BoardMembershipMap = {};
        (data as any[])?.forEach((row) => { map[row.collection_id] = true; });
        setMembership(map);
      } catch (e) {
        logger.error('Error fetching board membership', e, { adId });
      } finally {
        setLoading(false);
      }
    })();
  }, [adId]);

  /**
   * Toggle board membership for the current ad
   *
   * Creates a pending change that must be committed via commitChanges().
   * If `next` is provided, forces the membership to that value.
   * If omitted, toggles based on current effective value (including pending changes).
   *
   * @param collectionId - The collection/board ID to toggle
   * @param next - Optional boolean to force membership state (true = add, false = remove)
   */
  const toggleBoard = useCallback((collectionId: string, next?: boolean) => {
    setPendingChanges((prev) => {
      const effective = prev.hasOwnProperty(collectionId)
        ? prev[collectionId]
        : !!membership[collectionId];

      const target = typeof next === 'boolean' ? next : !effective;
      return { ...prev, [collectionId]: target };
    });
  }, [membership]);

  /**
   * Select all boards for the current ad
   *
   * Creates pending changes to add the ad to all specified collections.
   * Changes must be committed via commitChanges().
   *
   * @param collectionIds - Array of collection/board IDs to add the ad to
   */
  const selectAll = useCallback((collectionIds: string[]) => {
    setPendingChanges((prev) => {
      const updates = { ...prev };
      collectionIds.forEach((id) => {
        updates[id] = true;
      });
      return updates;
    });
  }, []);

  /**
   * Deselect all boards for the current ad
   *
   * Creates pending changes to remove the ad from all specified collections.
   * Changes must be committed via commitChanges().
   *
   * @param collectionIds - Array of collection/board IDs to remove the ad from
   */
  const deselectAll = useCallback((collectionIds: string[]) => {
    setPendingChanges((prev) => {
      const updates = { ...prev };
      collectionIds.forEach((id) => {
        updates[id] = false;
      });
      return updates;
    });
  }, []);

  /**
   * Select only specific boards for the current ad (exclusive selection)
   *
   * Creates pending changes to add the ad to specified collections while
   * removing it from all other collections. This is an exclusive operation:
   * only the provided collection IDs will be selected, all others will be deselected.
   * Changes must be committed via commitChanges().
   *
   * @param collectionIds - Array of collection/board IDs to add the ad to
   * @param allCollectionIds - Array of all available collection/board IDs
   */
  const selectOnly = useCallback((collectionIds: string[], allCollectionIds: string[]) => {
    setPendingChanges((prev) => {
      const updates = { ...prev };
      // Set all provided IDs to true
      collectionIds.forEach((id) => {
        updates[id] = true;
      });
      // Set all other IDs to false
      allCollectionIds.forEach((id) => {
        if (!collectionIds.includes(id)) {
          updates[id] = false;
        }
      });
      return updates;
    });
  }, []);

  /**
   * Check if the ad is in a specific board
   *
   * Returns the effective membership status, considering pending changes.
   * If there's a pending change for the collection, returns that value.
   * Otherwise returns the saved membership status from the database.
   *
   * @param collectionId - The collection/board ID to check
   * @returns Boolean indicating if the ad is (or will be) in the board
   */
  const isBoardChecked = useCallback((collectionId: string): boolean => {
    if (pendingChanges.hasOwnProperty(collectionId)) return pendingChanges[collectionId];
    return !!membership[collectionId];
  }, [membership, pendingChanges]);

  /**
   * Commit all pending board membership changes to the database
   *
   * Applies each pending change by either inserting or deleting records
   * in user_collection_items table. After successful commit, updates
   * local membership state and clears pending changes.
   *
   * @throws Error if database operations fail
   */
  const commitChanges = useCallback(async () => {
    if (!adId || Object.keys(pendingChanges).length === 0) return;
    try {
      // Apply each pending change
      for (const [collectionId, shouldBeInBoard] of Object.entries(pendingChanges)) {
        const isInBoard = !!membership[collectionId];

        if (shouldBeInBoard && !isInBoard) {
          const { error } = await supabase
            .from('user_collection_items' as any)
            .insert({ collection_id: collectionId, ad_id: adId });
          if (error) throw error;
        } else if (!shouldBeInBoard && isInBoard) {
          const { error } = await supabase
            .from('user_collection_items' as any)
            .delete()
            .eq('collection_id', collectionId)
            .eq('ad_id', adId);
          if (error) throw error;
        }
      }

      // Merge changes locally
      setMembership((prev) => {
        const updated = { ...prev };
        for (const [collectionId, shouldBeInBoard] of Object.entries(pendingChanges)) {
          if (shouldBeInBoard) updated[collectionId] = true;
          else delete updated[collectionId];
        }
        return updated;
      });
      setPendingChanges({});
    } catch (e) {
      logger.error('Error committing board membership changes', e, { adId, pendingChanges });
      throw e;
    }
  }, [adId, membership, pendingChanges]);

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const savedBoardsCount = Object.values(membership).filter(Boolean).length;

  return {
    membership,
    loading,
    isBoardChecked,
    toggleBoard,          // (id, next?)
    selectAll,            // (collectionIds: string[])
    deselectAll,          // (collectionIds: string[])
    selectOnly,           // (collectionIds: string[], allCollectionIds: string[])
    commitChanges,
    hasPendingChanges,
    pendingChanges,
    savedBoardsCount,
  };
}
